package com.exura.components.pageTypography

import androidx.compose.runtime.Composable
import com.exura.components.icons.UntiledIcon
import com.exura.components.loading.InlineLoaderVariant
import com.exura.components.loading.Loader
import com.exura.ui.Assets
import com.exura.ui.Dimens
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.VerticalAlign
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.modifiers.verticalAlign
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle

@Composable
internal fun PageNavigationHeaderSection(
    vararg path: String,
    modifier: Modifier = Modifier,
    variant: CssStyleVariant<PageNavigationHeaderKind>? = null,
    onBack: (() -> Unit)? = null,
    isPageWorking: Boolean = false,
) {
    Row(
        modifier = PageNavigationHeaderStyle.toModifier(variant).then(
            ExuraTextStyle.toModifier(ExuraPageHeaderTextVariant)
        )
            .then(modifier),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Dimens.pageHeaderNavControlsGap),
    ) {
        Box(
            modifier = Modifier
                .aspectRatio(1)
                .fillMaxHeight()
                .justifyContent(JustifyContent.Center)
                .cursor(if (onBack != null) Cursor.Pointer else Cursor.Default)
                .onClick { if (onBack != null) onBack() },
            contentAlignment = Alignment.Center,
        ) {
            if (onBack != null) {
                UntiledIcon(Assets.IconsUntiled.BACK)
            }
        }
        BreadCrumbsRibbon(*path, modifier = Modifier.weight(1f))

        Box(
            modifier = ExuraTextStyle
                .toModifier(ExuraSectionHeaderTextVariant)
                .aspectRatio(1)
                .fillMaxHeight()
        ) {
            if (isPageWorking) {
                Loader(
                    loaderVariant = InlineLoaderVariant,
                )
            }
        }
    }
}

@Composable
private fun BreadCrumbsRibbon(
    vararg path: String,
    modifier: Modifier = Modifier,
) {
    Box(modifier = modifier) {
        Box(
            modifier = Modifier
                .styleModifier {
                    property("direction", "rtl")
                },
        ) {
            Box(
                modifier = PageNavigationBreadcrumbsStyle
                    .toModifier(),
                ) {
                val pathFiltered = path.filter { it.isNotBlank() }
                pathFiltered.forEachIndexed { index, segment ->
                    val isLast = index == pathFiltered.lastIndex
                    SpanText(segment, modifier = Modifier.verticalAlign(VerticalAlign.Middle))

                    if (!isLast) {
                        UntiledIcon(
                            Assets.IconsUntiled.PATH_EXPANDED,
                            modifier = Modifier.padding(leftRight = Dimens.iconTextRelGapSizeDefault).verticalAlign(VerticalAlign.Middle)
                        )
                    }
                }
            }
        }
    }
}

sealed interface PageNavigationHeaderKind : ComponentKind

val PageNavigationHeaderStyle = CssStyle<PageNavigationHeaderKind> {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .fillMaxWidth()
            .padding(leftRight = Dimens.pageHeaderNavIndent, topBottom = Dimens.pageHeaderNavWrapping)
            .borderBottom(width = Dimens.pageHeaderSeparatorWidth, style = LineStyle.Solid, color = palette.border)
    }
}

val PageNavigationBreadcrumbsStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .whiteSpace(WhiteSpace.NoWrap)
            .overflow(Overflow.Hidden)
            .textOverflow(TextOverflow.Ellipsis)
            .display(DisplayStyle.InlineBlock)
    }
}
